import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Dialog, Button, TextField, Box, Typography, IconButton,
  Chip, Paper, InputAdornment, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SellIcon from '@mui/icons-material/Sell';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { addCardToCollection, updateCollectionCard, uploadCardPhotos } from './collectionApi';
import Loader from '../styles/Loader';
import { useCollection } from '../cards/collectionProvider/CollectionProvider';
import { useCurrency } from '../providers/currencyContext';
import { SOURCE_CURRENCY, currencySymbol, parseMoneyInput } from '../utils/currency';
import {
  CONDITIONS, CONDITION_COLORS, COLLECTION_TYPES, DEFAULT_CONDITION,
  normalizeCondition, getConditionPrice, getBasePrice, getFairTradePercent,
} from '../utils/cardPricing';
import { getMarketValueDifference } from '../utils/priceAdapter';
import { firstPhotoRejection } from '../utils/photoLimits';
import { getRarityBadge } from '../cards/cardDisplay';
import { trackEvent } from '../analytics/analytics';
import { useDismissible } from '../native/dismissibleStack';
import { useKeyboardInset } from '../native/keyboardInset';
import { claimRefusals } from '../utils/refusalNotice';
import { saveDraftSoon, writeDraft } from '../native/drafts';

/** [B-39] Shown on a listing restored after Android closed the app. */
export const PHOTO_AGAIN_MESSAGE =
  'The app closed before your photos were saved. Your listing is back, please add the photos again.';

const VAULT_OPTIONS = [
  {
    value: 'glory',
    label: 'Vault',
    subtitle: 'Shown on your profile.\nNever traded.',
    Icon: EmojiEventsIcon,
  },
  {
    value: 'trade',
    label: 'Trade Binder',
    subtitle: 'Offered for trades\nwith collectors nearby.',
    Icon: SwapHorizIcon,
  },
  {
    value: 'sale',
    label: 'For Sale',
    subtitle: 'For sale at a\nprice you set.',
    Icon: SellIcon,
  },
];

/**
 * Photo copy per destination.
 *
 * A photo is required for EVERY destination, the Vault included — the Vault is
 * not a private shelf, it is the showcase other collectors browse on your
 * profile (`users/components/UserProfile.jsx` renders it), and whether a card is
 * a graded slab or raw is something only a photo can say.
 *
 * What changes between destinations is the framing, not the requirement: a
 * trade/sale photo is a condition disclosure aimed at a counterparty, a Vault
 * photo is the card's primary image and the one strangers see.
 */
const CONDITION_PHOTO_COPY = {
  title: 'Condition Photos',
  blurb: "Show the card's condition — front, back, any wear. Traders see these as your claim, not proof. Up to 5 photos.",
  // Trade/For Sale is LISTING the card, new or already in the Vault (the
  // first-card "List it for trade" path edits an owned copy), so the copy must
  // not say "adding to your collection" about a card that is already there.
  missing: 'Add at least one condition photo before listing this card.',
};

const VAULT_PHOTO_COPY = {
  title: 'Vault Photos',
  blurb: "Your photo becomes this card's image in your Vault — the one other collectors see on your profile. Show the slab, or the raw card. Up to 5 photos.",
  missing: "Add at least one photo — it becomes this card's image in your Vault.",
};

const photoCopyFor = (collectionType) =>
  (collectionType === 'glory' ? VAULT_PHOTO_COPY : CONDITION_PHOTO_COPY);

// Icons per canonical grade — options themselves come from the shared scale
// so the modal can never drift from the rest of the app.
const CONDITION_ICONS = {
  near_mint: StarIcon,
  lightly_played: CheckCircleIcon,
  moderately_played: FavoriteBorderIcon,
  heavily_played: WarningAmberIcon,
  damaged: BrokenImageIcon,
};

const CONDITION_OPTIONS = CONDITIONS.map((c) => ({
  value: c.value,
  label: c.label,
  subtitle: c.desc,
  Icon: CONDITION_ICONS[c.value] || StarIcon,
}));

/* Large icon tile selector. [B-23] The tiles WRAP onto a second row instead
   of scrolling sideways: the scroll had a hidden scrollbar, so at phone width
   "For Sale" and the last condition tiles read as cut off at the right edge. */
// Each tile is a radio in a radiogroup (E2E audit F-11): before, they were
// bare clickable boxes, so a screen reader announced nothing selectable and a
// keyboard could not reach them at all.
const TileSelector = ({ options, value, onChange, accentColor = '#6b21a8', label }) => (
  <Box role="radiogroup" aria-label={label} sx={{
    display: 'grid', gap: 1.2,
    gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
  }}>
    {options.map(({ value: v, label, subtitle, Icon }) => {
      const active = value === v;
      return (
        <Box
          key={v}
          role="radio"
          aria-checked={active}
          aria-label={label}
          tabIndex={0}
          onClick={() => onChange(v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onChange(v);
            }
          }}
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'flex-start', textAlign: 'center',
            p: 1.2, borderRadius: '18px', cursor: 'pointer',
            border: active ? `2.5px solid ${accentColor}` : '2px solid rgba(107,33,168,0.10)',
            bgcolor: active ? `${accentColor}0f` : '#fafafa',
            transition: 'all 0.18s ease',
            userSelect: 'none',
            '&:hover': {
              border: `2px solid ${active ? accentColor : 'rgba(107,33,168,0.28)'}`,
              bgcolor: active ? `${accentColor}14` : 'rgba(107,33,168,0.04)',
            },
          }}
        >
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: active ? `${accentColor}18` : 'rgba(107,33,168,0.07)',
            mb: 1, transition: 'all 0.18s ease',
          }}>
            <Icon sx={{ fontSize: 22, color: active ? accentColor : '#94a3b8', transition: 'color 0.18s' }} />
          </Box>
          <Typography sx={{
            fontSize: '0.75rem', fontWeight: 800, color: active ? '#0f172a' : '#64748b',
            lineHeight: 1.25, mb: 0.5, transition: 'color 0.18s',
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontSize: '0.6rem', color: '#64748b', lineHeight: 1.45,
            whiteSpace: 'pre-line',
          }}>
            {subtitle}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const FairTradeMeter = ({ yourValue, marketValue }) => {
  const { formatAmount } = useCurrency();
  const pct = getFairTradePercent(yourValue, marketValue);
  // marketValue is the market estimate the ask is judged against — the same
  // figure the asking-price input carries as its placeholder — so the
  // percentage is a share of it, not of the two numbers' mean. The symmetric
  // getValueDifference stays with the trade builder, where neither side is a
  // reference.
  const { diff, pct: diffPct } = getMarketValueDifference(yourValue, marketValue);
  const isFair = Math.abs(diff) < 0.01;

  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#64748b' }}>
          Underpay
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#6b21a8' }}>
          Fair Trade
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#64748b' }}>
          Overpay
        </Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: 99, bgcolor: '#e2e8f0', position: 'relative', overflow: 'visible' }}>
        <Box sx={{
          position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)',
          left: `${pct}%`, width: 14, height: 14, borderRadius: '50%',
          bgcolor: isFair ? '#22c55e' : '#f97316',
          border: '2.5px solid #fff',
          boxShadow: `0 0 0 2px ${isFair ? '#22c55e' : '#f97316'}`,
          transition: 'left 0.3s ease',
        }} />
      </Box>
      <Typography sx={{ textAlign: 'center', mt: 1.2, fontSize: '0.72rem', fontWeight: 700, color: isFair ? '#22c55e' : '#f97316' }}>
        {isFair
          ? 'Perfect fair trade value'
          : `Value difference: ${diff > 0 ? '+' : ''}${formatAmount(Math.abs(diff), SOURCE_CURRENCY)} (${diffPct.toFixed(1)}%)`}
      </Typography>
    </Box>
  );
};

/**
 * Whether an upload result may be reused for the selection now on screen.
 *
 * True only when the two lists hold the same File objects, by reference, in the
 * same order. Both mutators build a brand-new array with different membership
 * (`handlePhotoSelect` appends, `handleRemovePhoto` filters), so adding or
 * removing a photo invalidates the cache on its own.
 *
 * Identity rather than a looser key: a length-only compare would let a
 * one-for-one swap reuse the URL of the photo the user just deleted and save
 * the wrong image. 're-uploads when the photo is swapped after a failure' in
 * ListCardModal.test.jsx is the control that holds that line.
 */
const sameFileSelection = (a, b) =>
  Array.isArray(a) && a.length === b.length && a.every((file, i) => file === b[i]);

/**
 * [#315] Whether a newly picked file is one the user has already attached.
 *
 * `sameFileSelection` above is deliberately NOT reused, and the difference is
 * not stylistic. That helper asks "is this the very same array of File objects
 * I already uploaded", where reference identity is exactly right for a cache
 * key. This one asks "has the user already given me this file", across separate
 * trips through the OS picker — and every trip mints brand-new `File` objects,
 * so two picks of one file are never the same object and `===` would answer no
 * every time.
 *
 * name + size + lastModified is the identity the platform actually exposes. It
 * can collide in principle — two genuinely different files, same name, same
 * byte count, same mtime — and the cost of a collision is that the second one
 * is silently not attached. That is stated rather than hidden: it is a real if
 * remote downside, traded for not uploading the same photo twice and spending
 * the 200 MB quota twice (see [#172], [#301]).
 */
const isSamePickedFile = (a, b) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/**
 * @param {object} [existingUserCard] The UserCard record when this card is
 *   already in the collection. Its presence switches the modal from "add" to
 *   "edit": the form is prefilled from the stored record and submits through
 *   PUT /collection/:cardId instead of POST.
 *
 *   Without it, changing a card's status ran the add form's blank defaults
 *   through the POST merge — which always sends a condition and always sends
 *   notes, so neither of the server's `|| existing` guards could fire. Moving a
 *   Heavily Played card into the Trade Binder re-graded it Near Mint, erased the
 *   owner's notes, and replaced their condition photos.
 */
// `initialCollectionType` lets a caller open the dialog on an owned card
// already pointed at a destination — the first-card "List it for trade" path
// opens it on the Trade Binder (P0 area B). Only the starting tile; the user can
// still pick any destination before confirming.
//
// [B-39] `draftUserId` (the signed-in user) turns on the listing draft: while
// the sheet is open on an owned card, what the user set is kept in storage, so
// Android closing the app behind the photo picker or camera does not lose it.
// `resume` is that draft read back by CollectionPage on the next launch.
const ListCardModal = ({ open, onClose, card, onSuccess, existingUserCard = null, initialCollectionType = null, draftUserId = null, resume = null }) => {
  const { collectionCount, isLoading: collectionLoading, maybeCelebrateFirstCard, revealCard } = useCollection();
  const isEdit = !!existingUserCard;
  const [collectionType, setCollectionType] = useState('glory');
  const [condition, setCondition] = useState(DEFAULT_CONDITION);
  const [askingPrice, setAskingPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /**
   * Photos already stored on the record. Kept apart from `photoFiles` (which is
   * this session's uploads) so "keep what's there" and "replace with these" stay
   * distinguishable — the submit only sends `photos` when the user picked new
   * ones, which is what stops a status change from wiping the originals.
   */
  const [existingPhotos, setExistingPhotos] = useState([]);
  const { formatAmount } = useCurrency();
  /**
   * The last successful upload of this modal session: `{ files, urls }`.
   *
   * A failed save leaves `photoFiles` in place and re-enables Confirm, so every
   * retry used to run `uploadCardPhotos` again. Each run stores a fresh row in
   * the upload ledger and charges its bytes against the account's quota, and
   * nothing releases the batch left behind by the attempt that failed — so a
   * save that cannot succeed (the edit path's 409 on an ambiguous copy is
   * deterministic, not a flaky link) drained the quota once per tap.
   *
   * Held in a ref rather than state because reading it must not depend on a
   * re-render landing between the failure and the next submit.
   */
  const uploadedPhotosRef = useRef(null);
  // [B-22] The asking price the user has already been warned about, so the
  // second tap on the same figure goes through.
  const farPriceOkRef = useRef(null);
  // [B-63] The AbortController of the save in progress, if any.
  const submitRef = useRef(null);
  const fileInputRef = useRef(null);
  // [B-39] This sheet was restored with a photo the app lost. Kept in the draft
  // until the sheet closes, so closing the app again before the photo is added
  // still brings the "add the photos again" message back.
  const photoLostRef = useRef(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  // Android 15+ (API 36) draws edge-to-edge and no longer resizes the WebView
  // when the IME opens. Asking Price and Notes are the last two fields in this
  // modal's form column, with Confirm Addition under them — the keyboard covers
  // all of it. See src/native/keyboardInset.js.
  const keyboardInset = useKeyboardInset();

  // Prefill from the stored record each time the modal opens on an owned card.
  // Keyed on `open` as well as the record so reopening after a cancel restores
  // the saved values rather than whatever the last edit left behind.
  useEffect(() => {
    if (!open) return;
    photoLostRef.current = false;
    if (existingUserCard) {
      setCollectionType(initialCollectionType || existingUserCard.collectionType || 'glory');
      setCondition(existingUserCard.condition || DEFAULT_CONDITION);
      setAskingPrice(
        existingUserCard.askingPrice != null ? String(existingUserCard.askingPrice) : ''
      );
      setNotes(existingUserCard.notes || '');
      setExistingPhotos(existingUserCard.photos || []);
      // [B-39] What the user had set before Android closed the app, over the
      // stored record. Only for the copy it was saved on.
      if (resume && resume.id === existingUserCard._id) {
        if (COLLECTION_TYPES.some((t) => t.value === resume.type)) setCollectionType(resume.type);
        if (typeof resume.cond === 'string') setCondition(resume.cond);
        if (typeof resume.ask === 'string') setAskingPrice(resume.ask);
        if (typeof resume.notes === 'string') setNotes(resume.notes);
        // The photo itself cannot come back: the picker hands it to a page that
        // no longer exists (see CollectionPage), so ask for it again.
        if (resume.p) {
          photoLostRef.current = true;
          setError(PHOTO_AGAIN_MESSAGE);
        }
      }
    } else {
      setCollectionType('glory');
      setCondition(DEFAULT_CONDITION);
      setAskingPrice('');
      setNotes('');
      setExistingPhotos([]);
    }
    // `resume` is read when the sheet opens, which is what these deps re-run on;
    // CollectionPage sets it in the same update that opens the sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingUserCard, initialCollectionType]);

  // [B-39] Keep the open listing in storage while it is being filled in. The
  // id of the copy and what the user set, nothing else. `p` marks a photo that
  // was picked (or was being picked) and will not survive the app closing.
  const draftId = open && draftUserId ? existingUserCard?._id : null;
  const photoCount = photoFiles.length;
  const listingDraft = (picking) => ({
    id: draftId, type: collectionType, cond: condition, ask: askingPrice, notes,
    p: picking || photoCount > 0 || photoLostRef.current ? 1 : 0,
  });
  useEffect(() => {
    if (draftId) saveDraftSoon('listing', draftUserId, listingDraft());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, draftUserId, collectionType, condition, askingPrice, notes, photoCount]);

  // The picker or camera covers the app, and that is when Android closes it, so
  // the draft is written now rather than after the debounce.
  const openPhotoPicker = () => {
    if (draftId) writeDraft('listing', draftUserId, listingDraft(true));
    fileInputRef.current?.click();
  };

  const { price: adjustedMarket, currency: adjustedCurrency, isEstimate } = useMemo(
    () => getConditionPrice(card, condition), [card, condition]);
  const { value: basePrice, currency: baseCurrency } = useMemo(
    () => getBasePrice(card), [card]);
  // The asking price is typed into a field labelled "(EUR)" and stored
  // untagged, so it can only be compared against a market figure that is also
  // in the source currency. A provider quote in dollars is not a yardstick for
  // a euro ask, and there is no approved rate to make it one.
  const askComparable = adjustedCurrency === SOURCE_CURRENCY;
  // Only a source-currency figure can seed a field whose value is STORED as
  // source currency; a dollar quote here would suggest an ask this input
  // cannot express.
  const askPlaceholder =
    askComparable && adjustedMarket > 0 ? adjustedMarket.toFixed(2) : '0.00';
  // [B-22] Through the shared money parser, not parseFloat: "12,50" typed on a
  // Samsung number pad is 12.5, and parseFloat would have read it as 12.
  // A price saved before this parser existed (the old number field took
  // "41.345") may not read back through it. Left untouched, it stands for the
  // stored number as is, so a grade or note edit on that listing still saves.
  const storedAsk = existingUserCard?.askingPrice;
  const askUntouched = storedAsk != null && askingPrice === String(storedAsk);
  const askNum = askUntouched ? Number(storedAsk) : parseMoneyInput(askingPrice) || 0;
  const rarityBadge = card ? getRarityBadge(card) : null;
  const rarityLabel = rarityBadge?.label ? rarityBadge.label.toUpperCase() : null;
  const isHolo = !!rarityBadge?.glow;

  // `condition` holds the RAW stored value, which on an older record may be a
  // legacy grade (`poor`, `excellent`, …) rather than a canonical one, so any
  // lookup keyed on the CANONICAL grades has to normalize first — exactly as
  // the other consumers do (CardProfileModal.jsx, TradeRequestModal.jsx).
  //
  // The colour already resolved correctly because CONDITION_COLORS is keyed by
  // ALL_CONDITION_VALUES (canonical + legacy); the label did not, because
  // CONDITIONS holds canonical grades only, so a legacy value missed every row
  // and fell through to 'NM'. The price under it is computed by
  // getConditionPrice → conditionMultiplier, which DOES normalize — so the
  // badge read "NM" above a damaged-adjusted price, describing two different
  // cards. `|| 'NM'` is now unreachable (normalizeCondition always returns a
  // canonical value) and is kept only as a defensive default.
  // [#275] ONE normalized value, shared by the label below and by the gate on
  // the conditioned price line (`normalizedCondition !== 'near_mint'`). They
  // used to disagree: the gate read the RAW grade, so a record stored as the
  // legacy `mint` — or as anything unrecognised, which normalizeCondition
  // sends to DEFAULT_CONDITION — opened the gate and was then labelled NM by
  // the line it had just opened, at conditionMultiplier's 1.0. On the estimate
  // path — the only one reachable today, since getVerifiedConditionPrice
  // rejects the USD rows the refresh cron writes — both lines then carry the
  // same figure, printing "Est. NM: $100.00" directly under
  // "Market (NM): $100.00". On the latent verified path the second line can
  // hold a DIFFERENT number, and suppressing it there loses that figure; that
  // is pre-existing behaviour for canonical near_mint, which this only makes
  // `mint` consistent with, and it is tracked separately rather than widened
  // here.
  //
  // `conditionColor` deliberately keeps reading the RAW value: CONDITION_COLORS
  // is keyed by ALL_CONDITION_VALUES, so legacy grades resolve there already.
  const normalizedCondition = normalizeCondition(condition);
  const conditionColor = CONDITION_COLORS[condition] || '#6b21a8';
  const conditionLabel = CONDITIONS.find(
    (c) => c.value === normalizedCondition
  )?.shortLabel || 'NM';

  // [#312] Both early returns below release the picker's selection with
  // `e.target.value = ''`. A file input fires `change` only when the selection
  // it is holding CHANGES, so after a refusal the user who taps Add Photos and
  // picks the very same file again gets no event at all — no handler runs and
  // nothing on screen answers the tap. Emptying the selection is what makes
  // that identical second pick deliverable.
  //
  // The success path is left alone to keep this change to the one branch that
  // is broken. Be careful about the reason: an earlier draft of this comment
  // claimed the browser's de-duplication "prevents duplicates today", and BOTH
  // refute-phase skeptics measured that false AT THE TIME. The input is
  // `multiple`, and until [#315] nothing here de-duplicated, so picking [A]
  // then [A,B] — or [A], [B], [A] — appended the same file twice. What the
  // BROWSER blocks on its own is only the single sequence of two IMMEDIATELY
  // CONSECUTIVE, byte-identical whole selections; the general case is now
  // covered by the de-duplication in `handlePhotoSelect` below, not by it.
  // Clearing on success would unblock that one sequence too; not clearing keeps
  // today's behaviour on a path [#312] never said was broken.
  //
  // [#314], TAKEN: `handleRemovePhoto` below releases the input too. Accept A ->
  // delete A's tile -> pick A again used to be the same dead end as [#312]'s,
  // reached by a gesture that needs no error message to arrive at. Releasing the
  // input is what makes that second pick deliverable.
  //
  // BE PRECISE ABOUT THE SCOPE — three refute-phase skeptics each measured an
  // earlier draft of this comment overstating it, so the narrowed claims are:
  //
  //   * The dead end is not unconditional. It exists only while the input still
  //     holds exactly the selection being re-picked. Pick [A], pick [B], delete
  //     A's tile, re-pick [A] already worked before this change, because the
  //     input was holding [B].
  //   * It blocked Confirm only on a card with no stored photos: the guard below
  //     is `photoFiles.length === 0 && existingPhotos.length === 0`, so a card
  //     that already holds photos was never trapped.
  //   * Clearing here CANNOT double the file that was removed — unless another
  //     copy of it is already in `photoFiles`. That WAS reachable, because
  //     nothing here de-duplicated: pick [A], [B], [A] — three tiles — delete
  //     the first A, re-pick [A], and A was on screen twice. Measured.
  //   * More generally: after the clear, ANY file still in `photoFiles` that
  //     reappeared in the next selection was appended again. That covered the
  //     multi-file case (pick [A,B], delete A, re-pick [A,B] -> B twice) and the
  //     two-single-picks case above; they were one rule, not two.
  //
  // [#315]/[#316], TAKEN — the LAST TWO bullets above are now HISTORY, and the
  // two that used to sit here with them are gone because they no longer
  // describe this file. (The first two bullets are NOT history: the dead end is
  // still conditional on what the input is holding, and it still blocks Confirm
  // only on a card with no stored photos. A refute-phase skeptic caught this
  // sentence claiming all four.) `handlePhotoSelect` de-duplicates its append
  // against
  // `photoFiles` before it counts, so:
  //
  //   * The "NOT strictly better in every shape" trade is retired FOR THE
  //     GESTURES [#314] NAMED: pick [A], pick [B], delete A's tile, re-pick [B]
  //     left B doubled, and now leaves B single. Do NOT read that as universal
  //     improvement — an earlier draft of this very sentence claimed exactly
  //     that and a skeptic falsified it by constructing a stale-error state the
  //     de-duplication had itself introduced. That is what the `setError('')`
  //     in the empty-`files` branch below is for.
  //   * The five-photo case is no longer a new bad outcome. Pick five, delete
  //     the fifth tile, re-pick the same five: the four survivors are filtered
  //     out, only the deleted file is fresh, 4 + 1 clears the count guard, and
  //     the file the user deleted comes back. It is recovered, not merely
  //     un-refused — [#316] predicted "a no-op", and the measured result is
  //     better than that.
  //
  // The success path is STILL not cleared. De-duplication makes clearing it
  // safe, which is the reason [#315] gave for wanting it, but doing it is a
  // separate behaviour change on a path nothing has reported as broken, so it
  // stays out of this fix.
  //
  // `ListCardModal.repick.test.jsx` pins the fix, and its "an ACCEPTED photo
  // keeps hold of the picker" block still holds the success path above
  // uncleared. It no longer pins the DOUBLING: [#315] removed the behaviour and
  // with it the only assertion that held it (that test's count went 3 -> 2), so
  // nothing anywhere asserts a duplicate tile any more. Said explicitly because
  // this sentence used to claim otherwise.
  //
  // (verification/CardChallengeModal.jsx:183 can clear unconditionally because
  // it refuses any multi-file selection at :184 and REPLACES its one `file`;
  // this picker appends to `photoFiles` below.)
  const handlePhotoSelect = (e) => {
    const picked = Array.from(e.target.files);

    // [#315] Attach only what is not attached already.
    //
    // This runs BEFORE the count guard, and the order is the whole point. With
    // it after, re-picking a five-file selection whose fifth tile was deleted
    // is measured as 4 + 5 and refused with "Maximum 5 photos allowed" printed
    // over four tiles — [#316]. Filtering first makes that same gesture add
    // back exactly the one file that was deleted.
    const files = picked.filter(
      (file) => !photoFiles.some((held) => isSamePickedFile(held, file))
    );

    // Everything picked is already on screen, so there is nothing to add.
    // Release the picker, so the next pick of the same selection is still
    // delivered rather than swallowed by the browser's own de-duplication —
    // and CLEAR THE ERROR.
    //
    // The clear is not tidiness. A refute-phase skeptic CONSTRUCTED the state
    // this branch leaves behind without it: pick four photos, pick two more to
    // trip "Maximum 5 photos allowed", then re-pick one of the four. Nothing is
    // added, and the stale message stands over FOUR tiles with room for a
    // fifth — which is [#316]'s symptom word for word, reached by a route
    // [#315] would not have closed. The size-rejection message strands the same
    // way. And an empty `change` event (a cancelled Android WebView pick can
    // deliver one) used to clear the error by falling through to the
    // `setError('')` on the append path; this early return took that away.
    //
    // So the omission was a regression THIS FIX INTRODUCED, not one it
    // inherited — which is why it is called out here rather than quietly fixed.
    //
    // Still deliberately without a NEW message: inventing copy for "you already
    // picked that one" is a judgement call, and it is filed as [#317].
    if (files.length === 0) {
      e.target.value = '';
      setError('');
      return;
    }

    if (photoFiles.length + files.length > 5) {
      setError('Maximum 5 photos allowed');
      e.target.value = '';
      return;
    }
    // [#24] Refuse what the server would refuse, before uploading it. See
    // `utils/photoLimits.js` -- the message is the server's own string.
    const rejection = firstPhotoRejection(files);
    if (rejection) {
      setError(rejection);
      e.target.value = '';
      return;
    }
    setPhotoFiles((prev) => [...prev, ...files]);
    setPhotoPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    setError('');
  };

  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
    // [#314] Let go of the picker's selection, or the file just deleted cannot
    // be picked again — see the comment above `handlePhotoSelect`.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!card?._id) return;
    setError('');
    // Every card in a collection carries a real photo — including vault. The
    // photo becomes the tile's primary image, so a card without one would look
    // half-finished next to its neighbours. A card already holding photos has
    // satisfied that once; demanding new ones to change a status is what forced
    // the old flow to overwrite them.
    if (photoFiles.length === 0 && existingPhotos.length === 0) {
      setError(photoCopyFor(collectionType).missing);
      return;
    }
    if (collectionType === 'sale') {
      // [B-22] A price the parser cannot read is refused, never shrunk to a
      // guess the way parseFloat did.
      if (askingPrice.trim() && !askUntouched && parseMoneyInput(askingPrice) === null) {
        setError('Enter a price like 12.50 or 12,50');
        return;
      }
      // [#177] A For Sale card must have a price above zero. An empty field
      // used to save as 0. This also stops an untouched stored 0 or null, so a
      // listing saved without a price gets one on its next edit. The server
      // refuses a For Sale save without a price in the same words.
      if (!(askNum > 0)) {
        setError('Set a price to list this card for sale.');
        return;
      }
      // A slipped decimal is a 10x or 100x error, so a price three times above
      // or below the market figure asks once before it goes public. The meter
      // already said so, but out of sight under the card preview.
      if (
        askComparable && adjustedMarket > 0 && askNum > 0 &&
        (askNum > adjustedMarket * 3 || askNum * 3 < adjustedMarket) &&
        farPriceOkRef.current !== askNum
      ) {
        farPriceOkRef.current = askNum;
        setError(`${formatAmount(askNum, SOURCE_CURRENCY)} is far from the market value of ${formatAmount(adjustedMarket, adjustedCurrency)}. If that is your price, tap again to confirm.`);
        return;
      }
    }
    setSaving(true);
    const wasEmpty = !collectionLoading && collectionCount === 0;
    // [B-63] This save's own abort switch. Closing the sheet (Back, X, Cancel,
    // the backdrop) flips it: the upload is aborted, and a save that was still
    // waiting on it never runs. Before, the upload finished ~20 s after the
    // sheet closed and the save replaced the card's photos anyway.
    const ctl = new AbortController();
    submitRef.current = ctl;
    try {
      let uploadedPhotos = [];
      if (photoFiles.length > 0) {
        const cached = uploadedPhotosRef.current;
        if (cached && sameFileSelection(cached.files, photoFiles)) {
          // These exact files are already on the server from an earlier attempt
          // in this session. Reuse their URLs rather than paying for them twice.
          uploadedPhotos = cached.urls;
        } else {
          setUploading(true);
          uploadedPhotos = await uploadCardPhotos(photoFiles, ctl.signal);
          // Closed while the response was on its way: nothing more to do.
          if (ctl.signal.aborted) return;
          setUploading(false);
          uploadedPhotosRef.current = { files: photoFiles, urls: uploadedPhotos };
        }
      }
      if (isEdit) {
        // Only what the form actually carries. `photos` is omitted entirely
        // unless the user uploaded new ones, so the stored ones survive a
        // status change. askingPrice is sent as null off 'sale' — the POST
        // merge's `askingPrice ?? existing` made it the one field a user could
        // never clear, so a card moved back to the Vault kept advertising a
        // price in the marketplace and in the trade fairness meter.
        const form = {
          collectionType,
          condition,
          askingPrice: collectionType === 'sale' ? askNum : null,
          notes,
        };
        // [B-64] ...and of that, only what the user CHANGED from the copy this
        // form was filled from. The PUT leaves an omitted field alone, so a note
        // written on another phone survives a grade change made here from a
        // sheet that never saw it. Sending every field wiped it.
        const stored = {
          ...existingUserCard,
          askingPrice: existingUserCard.askingPrice ?? null,
          notes: existingUserCard.notes || '',
        };
        const updates = {};
        Object.keys(form).forEach((k) => {
          if (form[k] !== stored[k]) updates[k] = form[k];
        });
        // Destination and price travel as a pair. The server does not couple
        // them, so a type change sent alone (the stale copy already had a null
        // price) left another phone's sale price on a Trade Binder card, and a
        // price sent alone could land on a card that phone took off sale.
        if ('collectionType' in updates || 'askingPrice' in updates) {
          updates.collectionType = form.collectionType;
          updates.askingPrice = form.askingPrice;
        }
        if (uploadedPhotos.length > 0) updates.photos = uploadedPhotos;
        // The COPY's id, not the catalog card's. PUT /collection/:id accepts
        // either, but a catalog id only resolves while the caller holds exactly
        // one copy — hold two and the server refuses with 409 AMBIGUOUS_COPY
        // ("Send the specific copy's id instead of the card id"), because there
        // is no honest way to guess which copy an edit meant. Sending
        // `card._id` therefore made every copy of a doubled-up card permanently
        // un-editable. `existingUserCard` IS that copy — it is what prefilled
        // this form — so the id was in scope the whole time.
        await updateCollectionCard(existingUserCard._id, updates);
      } else {
        await addCardToCollection({
          cardId: card._id,
          collectionType,
          condition,
          askingPrice: collectionType === 'sale' ? askNum : null,
          photos: uploadedPhotos,
          notes,
        });
      }
      const celebratedFirst = maybeCelebrateFirstCard({
        wasEmpty,
        cardId: card?._id,
        collectionType,
        // Celebrate with the user's just-taken photo — that's the emotional
        // moment ("here's *my* card"), not the stock catalog art. Fall back to
        // stock if the upload didn't return a URL.
        imageUrl:
          uploadedPhotos[0]?.url ||
          existingPhotos[0]?.url ||
          card?.images?.small ||
          card?.images?.large,
        cardName: card?.name,
      });

      // Never stack two full-screen moments. The first-card celebration wins
      // outright; the per-card reveal only runs when it did not fire.
      if (!celebratedFirst) {
        revealCard({
          card,
          ownerPhotoUrl: uploadedPhotos[0]?.url || existingPhotos[0]?.url,
        });
      }

      // "Listed" = made available to other traders (sale/trade); a glory add is
      // private, so card_added (server-tracked) covers that case on its own.
      if (collectionType === 'sale' || collectionType === 'trade') {
        trackEvent('collection_listed', {
          collectionType,
          cardId: card._id,
          askingPrice: collectionType === 'sale' ? askNum : null,
        });
      }

      // Say where the card went, so a page that shows one destination at a time
      // (the collection tabs) can follow it instead of staying on a tab the
      // card just left.
      if (onSuccess) onSuccess({ collectionType });
      handleClose();
    } catch (err) {
      // Shown in the sheet as `error` below, so no toast as well (B-31).
      claimRefusals();
      // [B-63] The user closed the sheet; an aborted upload is not news.
      if (ctl.signal.aborted) return;
      const message = typeof err === 'string' ? err : err.message || 'Failed to add card';
      // Drop the cache when the server refused the uploaded photos themselves.
      // Both writes this modal makes answer that with `handleError(res, 400,
      // "Those photos could not be verified. Upload them first.")` (server/
      // collection/collectionController.js, the POST and the PUT), and with no
      // error object — so errorHandling.js keeps the literal string, sends it as
      // `payload.error`, and extractError's `data?.error` branch rejects with it
      // verbatim. Retrying with storageKeys the server has just refused would
      // fail the same way, so the cache is worse than useless here.
      //
      // Matched on "photos could not be verified", not on "could not be
      // verified" alone: server/auth/authService.js answers an unverifiable
      // token with "Authentication Error: that token could not be verified.
      // Please log in again.", and that middleware guards these very routes
      // (collectionRoutes.js). Clearing on that 401 would re-run the upload this
      // cache exists to skip, for a failure that says nothing about the photos.
      if (/photos could not be verified/i.test(message)) {
        uploadedPhotosRef.current = null;
      }
      setError(message);
      setUploading(false);
    } finally {
      // After a close, handleClose has reset these, and a save started since
      // must keep its own spinner.
      if (!ctl.signal.aborted) setSaving(false);
    }
  };

  const handleClose = () => {
    // [B-39] Closed or saved: the listing must not come back on the next launch.
    writeDraft('listing');
    // [B-63] Abort an upload in flight, and with it the save waiting on it.
    submitRef.current?.abort();
    submitRef.current = null;
    setSaving(false);
    setUploading(false);
    photoPreviews.forEach(URL.revokeObjectURL);
    // The cache is scoped to one modal session, alongside the files it describes.
    uploadedPhotosRef.current = null;
    farPriceOkRef.current = null;
    setPhotoFiles([]);
    setPhotoPreviews([]);
    setCollectionType('glory');
    setCondition(DEFAULT_CONDITION);
    setAskingPrice('');
    setNotes('');
    setExistingPhotos([]);
    setError('');
    onClose();
  };

  // Android hardware Back closes the modal instead of leaving the collection.
  // Deliberately `handleClose` and not the raw `onClose` prop: that is what the
  // X, the backdrop and Escape already run, and it is the path that revokes the
  // preview object URLs. Routing Back around it would leak one blob per photo
  // the user picked and then backed out of.
  useDismissible(open, handleClose, 'list-card-modal');

  return (
    <Dialog
      // Named (E2E audit 7·17): with no DialogTitle, MUI pointed aria-labelledby
      // at an id nothing carries, so a screen reader announced only "dialog".
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        // MUI centres the dialog inside a full-height container. Padding the
        // bottom of that container by the keyboard height shrinks the box the
        // dialog centres in, which lifts it by exactly half the inset — which is
        // exactly what re-centres it in the strip of screen the keyboard leaves
        // visible. `box-sizing` is border-box app-wide via CssBaseline; stated
        // here because the arithmetic depends on it. 0 on web.
        '& .MuiDialog-container': {
          boxSizing: 'border-box',
          pb: `${keyboardInset}px`,
        },
      }}
      PaperProps={{ 'aria-label': isEdit ? 'Update this card' : 'Add to Collection',
        className: 'modal-enter',
        sx: {
          borderRadius: isMobile ? '1.5rem' : '2.5rem',
          overflow: 'hidden',
          boxShadow: '0 25px 60px -12px rgba(107,33,168,0.22), 0 8px 24px -8px rgba(0,0,0,0.10)',
          // Capped in viewport units rather than MUI's container-relative
          // `calc(100% - 64px)`, so shrinking the container above does NOT shrink
          // this — a 95vh Paper would keep its full height inside a container
          // that is now 95vh minus the keyboard, and overflow half of the
          // difference off the top of the screen. Subtracting the inset here is
          // what keeps the whole Paper inside the visible strip; the body (on a
          // phone) or the form column (wide) is the scroll container, so the
          // fields stay reachable. 0 on web,
          // where `calc(95vh - 0px)` is just 95vh.
          maxHeight: `calc(${isMobile ? '95vh' : '90vh'} - ${keyboardInset}px)`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* [B-23] On a phone THIS box scrolls, preview and form together, so the
          card preview scrolls away. It used to stay pinned over the top ~43%
          of the screen while the form scrolled in the strip below it. On a
          wide screen the two columns sit side by side and the form scrolls. */}
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        overflowX: 'hidden',
        overflowY: isMobile ? 'auto' : 'hidden',
        ...(!isMobile && { minHeight: 520 }),
      }}>

        {/* ── Left: Card Preview ── */}
        <Box sx={{
          width: isMobile ? '100%' : '40%',
          p: isMobile ? 3 : 5,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          flexShrink: isMobile ? 0 : undefined,
          background: 'linear-gradient(145deg, #f5f3ff 0%, #ede9fe 50%, #e8e0f0 100%)',
        }}>
          <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse at top right, rgba(107,33,168,0.10), transparent 60%)',
          }} />

          {card && (
            <>
              <Box sx={{ position: 'relative' }}>
                <Box
                  className={isHolo ? 'holo-effect' : undefined}
                  sx={{
                    width: isMobile ? 160 : 200, height: isMobile ? 224 : 280,
                    borderRadius: '18px', overflow: 'hidden',
                    boxShadow: '0 20px 48px -8px rgba(107,33,168,0.30), 0 4px 16px rgba(0,0,0,0.10)',
                    border: '2px solid rgba(255,255,255,0.8)',
                    bgcolor: '#fff',
                    transition: 'transform 0.4s ease',
                    '&:hover': { transform: 'translateY(-6px) rotate(-1deg)' },
                  }}
                >
                  <img
                    src={card.images?.large || card.images?.small || 'https://placehold.co/200x280?text=Card'}
                    alt={card.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>

                {/* Floating condition badge */}
                <Box sx={{
                  position: 'absolute', top: -12, right: -16,
                  bgcolor: conditionColor,
                  color: '#fff', px: 1.5, py: 0.7,
                  borderRadius: '12px',
                  boxShadow: `0 4px 16px ${conditionColor}66`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                }}>
                  <Typography sx={{ fontSize: '0.45rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.12em', lineHeight: 1, opacity: 0.85 }}>
                    SELLER-STATED
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, lineHeight: 1.3 }}>
                    {conditionLabel}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                {rarityLabel && (
                  <Typography sx={{
                    fontSize: '0.56rem', fontWeight: 900, letterSpacing: '0.2em',
                    color: '#6b21a8', mb: 0.5, textTransform: 'uppercase',
                  }}>
                    {rarityLabel}
                  </Typography>
                )}
                <Typography sx={{ fontSize: isMobile ? '1.1rem' : '1.35rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.2 }}>
                  {card.name}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.4 }}>
                  {card.set?.name}{card.number ? ` · #${card.number}` : ''}
                </Typography>
                {(basePrice > 0 || adjustedMarket > 0) && (
                  <>
                    {/* GATED, unlike the branch half of this hunk, which printed
                        the line unconditionally. `basePrice` is 0 for a card the
                        provider feed prices and Cardmarket does not — the outer
                        gate is open on `adjustedMarket` there — and an ungated
                        line renders "Market (NM): €0.00" for it, a confident
                        zero over a card we simply have no Cardmarket reference
                        for. */}
                    {basePrice > 0 && (
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#6b21a8', mt: 0.8 }}>
                        {/* `baseCurrency`, not a bare number: getBasePrice returns
                            {value, currency} and the Cardmarket branch is EUR
                            while a provider quote is USD. Dropping the code here
                            is what printed a dollar figure behind a euro sign. */}
                        Market (NM): {formatAmount(basePrice, baseCurrency)}
                      </Typography>
                    )}
                    {/* `normalizedCondition`, not the raw `condition`: a record
                        stored as a legacy grade (`mint`, `excellent`, …) — or as
                        anything unrecognised — opened this gate and was then
                        labelled NM by the very line it opened, at
                        conditionMultiplier's 1.0. The label above and this gate
                        now read the SAME normalized value ([#275]). */}
                    {adjustedMarket > 0 && normalizedCondition !== 'near_mint' && (
                      /* One qualifier, driven by `isEstimate` alone — the POLICY
                         cardDisplay.js:138-144 states, whose docblock reserves an
                         unqualified figure for a verified feed price.
                         FORM DIFFERS DELIBERATELY, and a skeptic was right to flag
                         that the first draft of this comment glossed it: the helper
                         qualifies the AMOUNT (`Est. $85.00`), this line qualifies
                         ahead of the GRADE LABEL (`Est. LP: $85.00`), because the
                         prefix position is inherited from the pre-fix line and moving
                         it is a wider copy change than this fix is entitled to make.
                         `{conditionLabel}: {isEstimate ? 'Est. ' : ''}{amount}` would
                         match the helper exactly and is the shape to adopt IF the
                         owner ever settles [#187] by routing this modal through
                         getPriceDisplay; it would also red legacyConditionBadge's
                         substring assertion, which is why it is not done here.
                         This line used to hardcode the `Est. ` prefix AND append
                         ` (est.)` when `isEstimate`, saying it twice on every real
                         card.
                         MERGE NOTE — the clause that used to follow ("so `isEstimate`
                         is true throughout") is now FALSE and has been removed rather
                         than kept as decoration: getVerifiedConditionPrice no longer
                         rejects a USD row. ALLOWED_PRICE_CURRENCIES is ["USD","EUR"],
                         so a row the cron stamps USD is now VERIFIED and this line
                         renders it unqualified, in dollars. That is the point of the
                         one-qualifier shape, not an accident of it.
                         [#187]'s vocabulary question — whether the "Market (NM)"
                         line above may call a multiplier estimate "Market" — is an
                         owner decision and is NOT touched here. */
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mt: 0.3 }}>
                        {isEstimate ? 'Est. ' : ''}{conditionLabel}: {formatAmount(adjustedMarket, adjustedCurrency)}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* ── Right: Form ── */}
        <Box sx={{
          width: isMobile ? '100%' : '60%',
          p: isMobile ? 3 : 4,
          display: 'flex', flexDirection: 'column',
          // [B-23] Full height on a phone; the box above does the scrolling.
          flex: isMobile ? 'none' : 1, minHeight: 0,
          bgcolor: '#fff',
          overflowY: isMobile ? 'visible' : 'auto',
          overflowX: isMobile ? 'visible' : 'hidden',
          WebkitOverflowScrolling: 'touch',
          pb: isMobile ? 'max(16px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' : undefined,
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#0f172a' }}>
                {isEdit ? 'Update this card' : 'Add to Collection'}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.2 }}>
                {isEdit ? 'Choose where it goes and grade the card.' : 'Choose a destination and grade the card.'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: '#94a3b8', bgcolor: '#f1f5f9', borderRadius: '10px',
                '&:hover': { bgcolor: 'rgba(107,33,168,0.08)', color: '#6b21a8' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Destination. [B-23] Was headed "Destination Vault", which read as
              the answer while Trade Binder was the tile selected. */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase',
              letterSpacing: '0.18em', color: '#64748b', mb: 1.2,
            }}>
              Destination
            </Typography>
            <TileSelector options={VAULT_OPTIONS} value={collectionType} onChange={setCollectionType} label="Destination" />
          </Box>

          {/* Condition Grade */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{
              fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase',
              letterSpacing: '0.18em', color: '#64748b', mb: 1.2,
            }}>
              Condition Grade
            </Typography>
            <TileSelector
              options={CONDITION_OPTIONS}
              value={condition}
              onChange={setCondition}
              label="Condition"
              accentColor={conditionColor}
            />
            <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mt: 1, lineHeight: 1.5 }}>
              This is your <b>stated</b> grade — traders see it as your claim.
              {collectionType !== 'glory'
                ? ' Condition photos below are also your claim — not proof.'
                : ''}
            </Typography>
          </Box>

          {/* Asking Price (sale only) */}
          {collectionType === 'sale' && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{
                fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase',
                letterSpacing: '0.18em', color: '#64748b', mb: 1.2,
              }}>
                Asking Price ({SOURCE_CURRENCY})
              </Typography>
              {/* [B-22] Text with a decimal keypad, not type="number": a number
                  input drops the comma a Samsung pad types, so "12,50" reached
                  this form as "1250". The parser above reads the raw text. */}
              <TextField
                fullWidth
                value={askingPrice}
                onChange={(e) => setAskingPrice(e.target.value)}
                placeholder={askPlaceholder}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {/* Source symbol, not the display one: this number is
                          stored and compared as a source-currency amount. */}
                      <Typography sx={{ fontWeight: 900, color: '#6b21a8' }}>
                        {currencySymbol(SOURCE_CURRENCY)}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                // Its own name: the placeholder is the market figure ("60.00"), which
                // is all a screen reader announced for it (E2E audit 17·9).
                inputProps={{ inputMode: 'decimal', 'aria-label': `Asking price (${SOURCE_CURRENCY})` }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px', bgcolor: 'rgba(248,247,250,0.8)', fontWeight: 700,
                    '& fieldset': { borderColor: 'rgba(107,33,168,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(107,33,168,0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#6b21a8', borderWidth: 2 },
                  },
                }}
              />
            </Box>
          )}

          {/* Fair trade meter for sale listings */}
          {/* Suppressed when the market figure is a provider quote in another
              currency: the ask is source-currency by construction, and a
              needle placed between two currencies would be a claim about a
              comparison nobody can make without a rate. */}
          {collectionType === 'sale' && askComparable && adjustedMarket > 0 && (
            <FairTradeMeter yourValue={askNum} marketValue={adjustedMarket} />
          )}

          {/* Photo upload — every destination, the Vault included. Hiding this
              for 'glory' while the submit still demanded a photo made the
              default selection a dead end: no control on screen could satisfy
              the guard, and the only way out was to file the card somewhere the
              user had not chosen. */}
          <Paper
            variant="outlined"
            data-testid="photo-section"
            sx={{
              p: 2.5, mt: 2, borderRadius: '16px',
              borderStyle: 'dashed', borderColor: 'rgba(107,33,168,0.18)',
              bgcolor: 'rgba(248,247,250,0.5)',
            }}
          >
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#64748b', mb: 0.5 }}>
              {photoCopyFor(collectionType).title}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mb: 1.5 }}>
              {photoCopyFor(collectionType).blurb}
            </Typography>

            {/* Stored photos, shown so the user can see this change keeps them.
                Adding new ones replaces the set; adding none leaves it alone. */}
            {existingPhotos.length > 0 && (
              <Box sx={{ mb: 1.5 }} data-testid="existing-photos">
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mb: 0.75 }}>
                  {photoFiles.length > 0
                    ? 'These will be replaced by the photos you just added.'
                    : 'Your current photos — they stay unless you add new ones.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', opacity: photoFiles.length > 0 ? 0.4 : 1 }}>
                  {existingPhotos.map((p, i) => (
                    <Box key={p.url || i} sx={{ width: 64, height: 64, borderRadius: '10px', overflow: 'hidden' }}>
                      <img
                        src={p.url}
                        alt={`Saved photo ${i + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            <input
              ref={fileInputRef}
              data-testid="photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={handlePhotoSelect}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={openPhotoPicker}
              disabled={photoFiles.length >= 5}
              sx={{
                borderRadius: '12px', borderColor: 'rgba(107,33,168,0.2)',
                color: '#6b21a8', fontWeight: 700, fontSize: '0.78rem',
                textTransform: 'none',
                '&:hover': { borderColor: '#6b21a8', bgcolor: 'rgba(107,33,168,0.04)' },
              }}
            >
              {photoFiles.length >= 5 ? 'Max reached' : 'Add Photos'}
            </Button>

            {photoPreviews.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                {photoPreviews.map((url, i) => (
                  <Box key={i} sx={{ position: 'relative', width: 64, height: 64, borderRadius: '10px', overflow: 'hidden' }}>
                    <img src={url} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <IconButton
                      size="small"
                      onClick={() => handleRemovePhoto(i)}
                      sx={{
                        position: 'absolute', top: 2, right: 2,
                        bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', p: 0.3,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Notes */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
              <Typography sx={{
                fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase',
                letterSpacing: '0.18em', color: '#64748b',
              }}>
                Notes
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1' }}>
                {notes.length}/200
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder="Notes (optional)"
              multiline
              rows={2}
              value={notes}
              onChange={(e) => notes.length < 200 || e.target.value.length < notes.length ? setNotes(e.target.value) : null}
              inputProps={{ maxLength: 200 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px', bgcolor: 'rgba(248,247,250,0.8)',
                  '& fieldset': { borderColor: 'rgba(107,33,168,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(107,33,168,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#6b21a8', borderWidth: 2 },
                },
              }}
            />
          </Box>

          {/* role=alert: focus stays on Confirm, so without it a screen reader
              heard nothing, and the second tap that a far-price warning asks
              for went through unheard (E2E audit 17·9). */}
          {error && (
            <Typography role="alert" sx={{ color: '#ef4444', fontSize: '0.82rem', fontWeight: 600, mt: 1.5 }}>
              {error}
            </Typography>
          )}

          {/* Actions */}
          <Box sx={{ mt: 'auto', pt: 3, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || uploading}
              startIcon={
                uploading || saving
                  ? <Loader context="inline" inheritColor label="Saving" />
                  : null
              }
              sx={{
                py: 1.9, borderRadius: '16px',
                bgcolor: '#6b21a8', fontWeight: 900,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                textTransform: 'uppercase',
                boxShadow: '0 8px 24px rgba(107,33,168,0.35)',
                '&:hover': {
                  bgcolor: '#7c2dc0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(107,33,168,0.4)',
                },
                '&:active': { transform: 'scale(0.97)' },
                transition: 'all 0.2s ease',
              }}
            >
              {uploading ? 'Uploading...' : saving ? 'Saving...' : isEdit ? 'Save' : 'Confirm Addition'}
            </Button>
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                py: 1.5, borderRadius: '16px', color: '#ef4444',
                fontWeight: 700, textTransform: 'uppercase',
                fontSize: '0.78rem', letterSpacing: '0.08em',
                border: '1.5px solid rgba(239,68,68,0.15)',
                '&:hover': { bgcolor: 'rgba(239,68,68,0.04)', borderColor: 'rgba(239,68,68,0.3)' },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ListCardModal;
